import {BASE_URL} from "../../config/network";
import {
    createAccessoriesDraftOrderMock,
    createAccessoriesPaymentMock,
    getAccessoriesOrderMock,
    preflightAccessoriesCheckoutMock
} from "../../mock/accessoriesCheckoutMock";

const JSON_HEADERS = {
    Accept: "application/json",
    "Content-Type": "application/json",
};

const buildHeaders = ({authToken = null, idempotencyKey = ""} = {}) => {
    const headers = {...JSON_HEADERS};
    if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
    }
    if (idempotencyKey) {
        headers["Idempotency-Key"] = idempotencyKey;
    }
    return headers;
};

const parseJsonSafe = async (response) => {
    try {
        return await response.json();
    } catch (_error) {
        return null;
    }
};

const shouldFallbackToMock = ({status, error}) => {
    if (error && !status) return true;
    return [404, 405, 500, 501, 502, 503, 504].includes(Number(status));
};

const createIdempotencyKey = (prefix = "acc") => {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
        return `${prefix}-${window.crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const preflightAccessoriesCheckoutAPI = async ({
    payload,
    authToken = null,
    catalog = {},
}) => {
    const url = `${BASE_URL}/public/attributes/accessories/checkout/preflight`;
    const idempotencyKey = createIdempotencyKey("acc-preflight");

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: buildHeaders({authToken, idempotencyKey}),
            body: JSON.stringify(payload || {}),
        });

        if (!response.ok) {
            const body = await parseJsonSafe(response);
            if (shouldFallbackToMock({status: response.status})) {
                return preflightAccessoriesCheckoutMock({payload, catalog, authToken});
            }
            const message = body?.message || `HTTP error: ${response.status}`;
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        if (shouldFallbackToMock({error})) {
            return preflightAccessoriesCheckoutMock({payload, catalog, authToken});
        }
        throw error;
    }
};

const createAccessoriesDraftOrderAPI = async ({
    payload,
    contact = null,
    customerId = null,
    authToken = null,
    idempotencyKey = "",
    catalog = {},
}) => {
    const url = `${BASE_URL}/public/attributes/accessories/orders/draft`;
    const stableIdempotencyKey = idempotencyKey || createIdempotencyKey("acc-draft");
    const requestBody = {
        accessories_purchase: payload?.accessories_purchase || [],
        accessories_rent: payload?.accessories_rent || [],
        contact,
        customer_id: customerId ?? null,
    };

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: buildHeaders({authToken, idempotencyKey: stableIdempotencyKey}),
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            const body = await parseJsonSafe(response);
            if (shouldFallbackToMock({status: response.status})) {
                return createAccessoriesDraftOrderMock({
                    payload,
                    catalog,
                    contact,
                    customerId,
                    authToken,
                    idempotencyKey: stableIdempotencyKey,
                });
            }
            const message = body?.message || `HTTP error: ${response.status}`;
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        if (shouldFallbackToMock({error})) {
            return createAccessoriesDraftOrderMock({
                payload,
                catalog,
                contact,
                customerId,
                authToken,
                idempotencyKey: stableIdempotencyKey,
            });
        }
        throw error;
    }
};

const createAccessoriesOrderPaymentAPI = async ({
    orderId,
    returnUrl,
    cancelUrl,
    webhookUrl,
    authToken = null,
    idempotencyKey = "",
}) => {
    const stableIdempotencyKey = idempotencyKey || createIdempotencyKey("acc-payment");
    const url = `${BASE_URL}/public/attributes/accessories/orders/${orderId}/payment`;
    const requestBody = {
        return_url: returnUrl,
        cancel_url: cancelUrl,
        webhook_url: webhookUrl,
    };

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: buildHeaders({authToken, idempotencyKey: stableIdempotencyKey}),
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            const body = await parseJsonSafe(response);
            if (shouldFallbackToMock({status: response.status})) {
                return createAccessoriesPaymentMock({
                    orderId,
                    returnUrl,
                    cancelUrl,
                    idempotencyKey: stableIdempotencyKey,
                });
            }
            const message = body?.message || `HTTP error: ${response.status}`;
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        if (shouldFallbackToMock({error})) {
            return createAccessoriesPaymentMock({
                orderId,
                returnUrl,
                cancelUrl,
                idempotencyKey: stableIdempotencyKey,
            });
        }
        throw error;
    }
};

const getAccessoriesOrderAPI = async ({orderId, authToken = null}) => {
    const url = `${BASE_URL}/public/attributes/accessories/orders/${orderId}`;
    try {
        const response = await fetch(url, {
            method: "GET",
            headers: buildHeaders({authToken}),
        });

        if (!response.ok) {
            if (shouldFallbackToMock({status: response.status})) {
                return getAccessoriesOrderMock({orderId});
            }
            const body = await parseJsonSafe(response);
            const message = body?.message || `HTTP error: ${response.status}`;
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        if (shouldFallbackToMock({error})) {
            return getAccessoriesOrderMock({orderId});
        }
        throw error;
    }
};

export {
    preflightAccessoriesCheckoutAPI,
    createAccessoriesDraftOrderAPI,
    createAccessoriesOrderPaymentAPI,
    getAccessoriesOrderAPI,
    createIdempotencyKey,
};
